import React from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, InfoCard, IconBadge, GradientButton } from '../../components/common/Primitives';
import { getTeamPortrait } from '../../lib/teamPortraits';
import { ABOUT } from '../../lib/mock2';

export default function About() {
  return (
    <PageShell>
      <section className="px-6 pt-12 pb-16">
        <div className="mx-auto max-w-[1240px]">
          <h1 className="text-[42px] md:text-[52px] font-extrabold leading-[1.06] tracking-tight text-navy">{ABOUT.hero.title}</h1>
          <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">{ABOUT.hero.desc}</p>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="PURPOSE" title="Building innovators, problem-solvers and future scientists for Viksit Bharat" desc="Gyan Dhara exists to contribute to Viksit Bharat by nurturing students who can think scientifically, question assumptions, solve unfamiliar problems and create new possibilities." />
          <div className="mt-6 rounded-2xl bg-white border border-canvas-200 p-6 shadow-soft">
            <div className="text-[15px] font-bold text-navy">Our purpose</div>
            <p className="mt-3 text-[14.5px] leading-relaxed text-navy">Enable students from every type of school to become confident thinkers, investigators and creators. Gyan Dhara strengthens conceptual clarity, accurate mental models, scientific curiosity and the ability to apply knowledge—foundations for future innovators, problem-solvers and scientists.</p>
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="PRINCIPLES" title="The principles that guide the product" desc="The previous long list is simplified to the principles that accurately describe the current platform." />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ABOUT.principles.map((p,i)=>(<InfoCard key={i} icon={p.icon} color={p.color} title={p.title} desc={p.desc} />))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="THE ECOSYSTEM" title="Gyan Dhara-verse" desc="The named spaces remain useful, but are explained only after the product is understood plainly." />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ABOUT.spaces.map((s,i)=>(<div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft"><div className="text-[15px] font-bold text-navy">{s.name}</div><p className="mt-1 text-[13px] text-ink-500">{s.desc}</p></div>))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="ORIGIN" title="Developed at IIT Guwahati" desc="Conceptualized, designed and developed by Dr. Keyur Sorathia & team at the Embedded Interaction Lab, Department of Design, IIT Guwahati, with contributions from school teachers, students, designers, developers and researchers." />
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="aspect-[16/10] overflow-hidden rounded-2xl border border-canvas-200 bg-black shadow-soft">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_wix-redesign-2/artifacts/1bootw0n_IMG-20260522-WA0004.jpg"
                alt="Gyan Dhara team at IIT Guwahati"
                loading="lazy"
                className="h-full w-full object-cover"
                data-testid="about-developed-team-image"
              />
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="text-[15px] font-bold text-navy">How the work is developed</div>
              <p className="mt-3 text-[13.5px] text-ink-700 leading-relaxed">Teacher consultations identify difficult concepts. The team translates these needs into immersive modules, evaluates them in schools, and uses findings to refine the learning experience.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team and collaborators */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="PEOPLE" title="Team and collaborators" desc="Keep the team visible for credibility, but separate the current core team from teacher collaborators and past contributors." />
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { name: 'Dr. Keyur Sorathia', role: 'Principal Investigator · Associate Professor, Design, IIT Guwahati', gradient: 'from-royal to-purple' },
              { name: 'Ganesh Kumar',        role: 'Lead Unity Developer',    gradient: 'from-cyan to-royal' },
              { name: 'Abhishek Manraj',     role: 'UX Designer',              gradient: 'from-purple to-cyan' },
              { name: 'Trishala Jain',       role: 'UX Designer',              gradient: 'from-mint to-royal' },
            ].map((m,i)=>{
              const portrait = getTeamPortrait(m.name);
              return (
                <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-4 shadow-soft">
                  {portrait
                    ? <div className="aspect-square rounded-xl overflow-hidden bg-canvas-100"><img src={portrait} alt={m.name} loading="lazy" className="h-full w-full object-cover object-top" /></div>
                    : <div className={`aspect-square rounded-xl bg-gradient-to-br ${m.gradient} grid place-items-center text-white text-[11px] font-bold`}>PHOTO</div>}
                  <div className="mt-4"><div className="text-[14.5px] font-bold text-navy">{m.name}</div><div className="mt-1 text-[12.5px] text-ink-500 leading-snug">{m.role}</div></div>
                </div>
              );
            })}
          </div>
          <div className="mt-8 flex justify-center"><GradientButton to="/team" variant="outline">View Team & Collaborators</GradientButton></div>
        </div>
      </section>
    </PageShell>
  );
}
